<script setup lang="ts">
import {ref, computed, watch} from 'vue'
import {useField} from 'vee-validate'
import api from "../api.ts";

const props = defineProps<{
  name: string
  currentUsername?: string
}>()

const model = defineModel<string>()

const username = useField<string>(props.name)
const usernameAvailable = ref<boolean | null>(null)
const checkingUsername = ref(false)
const usernameCheckTimeout = ref<ReturnType<typeof setTimeout>>()

const forbiddenUsernames = [
  'admin', 'root', 'null', 'undefined', 'test', 'username', 'user', 'guest',
  'support', 'superuser', 'api', 'system', 'info', 'contact'
]

const getUsernameMessages = computed(() => {
  if (username.errorMessage.value) {
    return [username.errorMessage.value]
  }

  const currentValue = username.value.value?.toLowerCase()
  if (forbiddenUsernames.includes(currentValue)) {
    return ['This username is not allowed. Please choose another one.']
  }

  if (usernameAvailable.value === false) {
    return ['This username is already taken.']
  }

  if (usernameAvailable.value === true) {
    return ['Great choice! This username is available.']
  }

  return []
})

watch(() => username.value.value, (val) => {
  if (val !== model.value) {
    model.value = val
  }
})
watch(() => model.value, (val) => {
  if (val !== username.value.value) {
    username.value.value = val
  }
})

watch(() => username.value.value, (newValue) => {
  model.value = newValue
  usernameAvailable.value = null

  if (usernameCheckTimeout.value) {
    clearTimeout(usernameCheckTimeout.value)
  }

  if (!newValue || newValue.length < 3) return

  if(props.currentUsername && newValue.toLowerCase() === props.currentUsername.toLowerCase()) {
    usernameAvailable.value = true
    return
  }

  usernameCheckTimeout.value = setTimeout(async () => {
    checkingUsername.value = true

    try {
      const res = await api.get('/api/v4/users/username-available', {
        params: { username: newValue }
      })
      usernameAvailable.value = res.data.available
    } catch (error) {
      console.error('Username check failed', error)
      usernameAvailable.value = null
    } finally {
      checkingUsername.value = false
    }
  }, 500)
})

defineExpose({
  errorMessage: username.errorMessage,
  value: username.value,
  usernameAvailable,
})
</script>

<template>
  <v-text-field
      class="my-2"
      label="Username*"
      placeholder="your-unique-username"
      v-model="username.value.value"
      :append-inner-icon="
      checkingUsername
        ? 'mdi-loading'
        : usernameAvailable === true
          ? 'mdi-check-circle'
          : usernameAvailable === false
            ? 'mdi-close-circle'
            : ''
    "
      :error="!!username.errorMessage.value || usernameAvailable === false"
      :success="!username.errorMessage.value && usernameAvailable === true"
      :color="usernameAvailable === true && !username.errorMessage.value ? 'success' : undefined"
      :messages="getUsernameMessages"
  />
</template>
